import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { BandeauCouverture } from '../components/planning/BandeauCouverture'
import { GrilleSemaine } from '../components/planning/GrilleSemaine'
import { PanneauAReaffecter } from '../components/planning/PanneauAReaffecter'
import { PanneauIntervention } from '../components/planning/PanneauIntervention'
import { Bouton, Etiquette, Liste, Pastille, Saisie } from '../components/ui/Base'
import { Bandeau, EtatVide, MessageErreur } from '../components/ui/Etats'
import { Modale } from '../components/ui/Modale'
import { SqueletteGrille } from '../components/ui/Squelette'
import { useToast } from '../components/ui/Toasts'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import { ApiError } from '../lib/api/erreurs'
import type {
  InterventionPlanning,
  ModificationIntervention,
  Page,
  SemainePlanning,
  TypeSoin,
} from '../lib/api/types'
import { useSession } from '../lib/auth'
import { formatDate, formatDuree } from '../lib/formatters'
import { ajouterJours, aujourdhui, lundiDeLaSemaine } from '../lib/temps'

interface ConflitEnAttente {
  message: string
  rejouer: () => void
}

export function Planning() {
  const [parametres, setParametres] = useSearchParams()
  const queryClient = useQueryClient()
  const toast = useToast()
  const { utilisateur } = useSession()

  // Les filtres vivent dans l'URL : un planning filtré doit être partageable
  // par lien et survivre à un rechargement. Un état React seul perdrait les
  // deux.
  const lundi = lundiDeLaSemaine(parametres.get('lundi') ?? aujourdhui())
  const filtreInfirmiers = parametres.getAll('infirmier')
  const recherchePatient = parametres.get('patient') ?? ''
  const filtreStatut = parametres.get('statut') ?? ''

  const [selection, setSelection] = useState<InterventionPlanning | null>(null)
  const [conflit, setConflit] = useState<ConflitEnAttente | null>(null)

  // Un remplaçant consulte le planning global mais ne le modifie pas (R6).
  const modifiable = utilisateur?.role === 'TITULAIRE' || utilisateur?.role === 'COLLABORATEUR'

  const requeteSemaine = useQuery({
    queryKey: cles.planning.semaine(lundi),
    queryFn: ({ signal }) => api.get<SemainePlanning>(`/planning/semaine?lundi=${lundi}`, signal),
  })

  const requeteSoins = useQuery({
    queryKey: cles.typesSoins.liste(),
    queryFn: ({ signal }) => api.get<Page<TypeSoin>>('/types-soins', signal),
    staleTime: 5 * 60_000,
  })

  const semaine = requeteSemaine.data

  /**
   * Filtrage mémoïsé, par jour.
   *
   * C'est ici qu'est le vrai coût d'un planning, et non dans le DOM : sans ce
   * `useMemo`, `interventions.filter(...)` s'exécute une fois par bloc à
   * chaque frappe dans le champ de recherche — 300 fois pour rien.
   */
  const interventionsParJour = useMemo(() => {
    if (!semaine) return []
    const recherche = recherchePatient.trim().toLowerCase()
    const mots = recherche ? recherche.split(/\s+/) : []

    return semaine.jours.map((jour) =>
      jour.interventions.filter((intervention) => {
        if (filtreInfirmiers.length > 0) {
          const cible = intervention.utilisateur_id ?? 'non-affectee'
          if (!filtreInfirmiers.includes(cible)) return false
        }
        if (filtreStatut && intervention.statut !== filtreStatut) return false
        if (mots.length > 0) {
          const nom = `${intervention.patient_nom} ${intervention.patient_prenom}`.toLowerCase()
          if (!mots.every((m) => nom.includes(m))) return false
        }
        return true
      }),
    )
  }, [semaine, filtreInfirmiers, filtreStatut, recherchePatient])

  const modifier = useMutation({
    mutationFn: ({
      id,
      corps,
      forcer,
    }: {
      id: string
      corps: ModificationIntervention
      forcer?: boolean
    }) => api.patch<InterventionPlanning>(`/interventions/${id}${forcer ? '?forcer=true' : ''}`, corps),

    onMutate: async ({ id, corps }) => {
      // `cancelQueries` AVANT `setQueryData` : sans lui, un rechargement déjà
      // en vol arrive après la mise à jour optimiste et l'écrase.
      await queryClient.cancelQueries({ queryKey: cles.planning.semaine(lundi) })
      const precedent = queryClient.getQueryData<SemainePlanning>(cles.planning.semaine(lundi))

      if (precedent) {
        // On n'applique de façon optimiste que les champs dont la forme est
        // identique des deux côtés. `soins` ne l'est pas : la mutation envoie
        // des identifiants, la réponse rend des objets complets avec libellé
        // et couleur. L'appliquer ici afficherait des pastilles vides le temps
        // de l'aller-retour — on attend donc la réponse du serveur pour lui.
        const positionnel = {
          ...(corps.date !== undefined ? { date: corps.date } : {}),
          ...(corps.heure_debut !== undefined ? { heure_debut: corps.heure_debut } : {}),
          ...(corps.utilisateur_id !== undefined ? { utilisateur_id: corps.utilisateur_id } : {}),
          ...(corps.duree_minutes !== undefined ? { duree_minutes: corps.duree_minutes } : {}),
          ...(corps.statut !== undefined ? { statut: corps.statut } : {}),
        }
        queryClient.setQueryData<SemainePlanning>(cles.planning.semaine(lundi), {
          ...precedent,
          jours: precedent.jours.map((jour) => ({
            ...jour,
            interventions: jour.interventions.map((i) =>
              i.id === id ? { ...i, ...positionnel, updated_at: new Date().toISOString() } : i,
            ),
          })),
        })
      }
      return { precedent }
    },

    onError: (erreur, _variables, contexte) => {
      // Le retour arrière PRÉCÈDE l'ouverture du dialogue : sinon l'utilisateur
      // voit l'intervention déplacée *et* un message disant que c'est
      // impossible, ce qui est incompréhensible.
      if (contexte?.precedent) {
        queryClient.setQueryData(cles.planning.semaine(lundi), contexte.precedent)
      }
      if (erreur instanceof ApiError && erreur.estConflitPlanning) {
        setConflit({
          message: erreur.message,
          rejouer: () => {
            const variables = _variables
            modifier.mutate({ ...variables, forcer: true })
            setConflit(null)
          },
        })
        return
      }
      toast(erreur instanceof Error ? erreur.message : 'Modification impossible.', 'erreur')
    },

    onSuccess: (_donnees, variables) => {
      if (variables.forcer) toast('Déplacement forcé — le conflit a été journalisé.', 'info')
    },

    // Invalider `["planning"]` en entier, pas seulement la semaine : un
    // déplacement change la couverture, et parfois la semaine suivante.
    onSettled: () => queryClient.invalidateQueries({ queryKey: cles.planning.tout }),
  })

  const changerParametre = useCallback(
    (cle: string, valeur: string | string[] | null) => {
      const suivants = new URLSearchParams(parametres)
      suivants.delete(cle)
      if (Array.isArray(valeur)) valeur.forEach((v) => suivants.append(cle, v))
      else if (valeur) suivants.set(cle, valeur)
      setParametres(suivants, { replace: true })
    },
    [parametres, setParametres],
  )

  const deplacer = useCallback(
    (intervention: InterventionPlanning, date: string, heure: string) => {
      modifier.mutate({ id: intervention.id, corps: { date, heure_debut: heure } })
    },
    [modifier],
  )

  if (requeteSemaine.isPending) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 animate-pulse rounded bg-slate-200" />
        <SqueletteGrille />
      </div>
    )
  }

  if (requeteSemaine.isError) {
    return <MessageErreur erreur={requeteSemaine.error} onReessayer={() => requeteSemaine.refetch()} />
  }

  if (!semaine) return null

  const totalAffiche = interventionsParJour.reduce((s, j) => s + j.length, 0)

  return (
    <div className="space-y-4">
      {/* ── En-tête et navigation ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            Semaine du {formatDate(semaine.lundi)} au {formatDate(semaine.dimanche)}
          </h1>
          <p className="text-sm text-slate-600">
            {semaine.totaux.nb_interventions} interventions ·{' '}
            {semaine.totaux.nb_non_affectees} à réaffecter
            {semaine.totaux.nb_conflits > 0 ? ` · ${semaine.totaux.nb_conflits} conflits` : ''}
            {totalAffiche !== semaine.totaux.nb_interventions
              ? ` · ${totalAffiche} affichées par les filtres`
              : ''}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Bouton
            variante="secondaire"
            onClick={() => changerParametre('lundi', ajouterJours(lundi, -7))}
            aria-label="Semaine précédente"
          >
            ←
          </Bouton>
          <Bouton variante="secondaire" onClick={() => changerParametre('lundi', aujourdhui())}>
            Aujourd&apos;hui
          </Bouton>
          <Bouton
            variante="secondaire"
            onClick={() => changerParametre('lundi', ajouterJours(lundi, 7))}
            aria-label="Semaine suivante"
          >
            →
          </Bouton>
          <input
            type="date"
            value={lundi}
            onChange={(e) => changerParametre('lundi', e.target.value)}
            aria-label="Choisir une semaine"
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          />
        </div>
      </div>

      {/* ── Filtres ── */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3">
        <div className="min-w-56 flex-1">
          <label htmlFor="filtre-patient" className="mb-1 block text-xs font-medium text-slate-600">
            Rechercher un patient
          </label>
          <Saisie
            id="filtre-patient"
            type="search"
            placeholder="Nom ou prénom"
            value={recherchePatient}
            onChange={(e) => changerParametre('patient', e.target.value || null)}
          />
        </div>

        <div>
          <label htmlFor="filtre-statut" className="mb-1 block text-xs font-medium text-slate-600">
            Statut
          </label>
          <Liste
            id="filtre-statut"
            value={filtreStatut}
            onChange={(e) => changerParametre('statut', e.target.value || null)}
          >
            <option value="">Tous</option>
            <option value="PLANIFIEE">Planifiée</option>
            <option value="CONFIRMEE">Confirmée</option>
            <option value="REALISEE">Réalisée</option>
            <option value="ANNULEE">Annulée</option>
            <option value="PATIENT_ABSENT">Patient absent</option>
          </Liste>
        </div>

        <fieldset className="flex flex-wrap items-center gap-1.5">
          <legend className="mb-1 w-full text-xs font-medium text-slate-600">Infirmiers</legend>
          {semaine.infirmiers.map((infirmier) => {
            const actif = filtreInfirmiers.includes(infirmier.id)
            return (
              <button
                key={infirmier.id}
                type="button"
                aria-pressed={actif}
                onClick={() =>
                  changerParametre(
                    'infirmier',
                    actif
                      ? filtreInfirmiers.filter((x) => x !== infirmier.id)
                      : [...filtreInfirmiers, infirmier.id],
                  )
                }
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${
                  actif ? 'border-sky-500 bg-sky-50 text-sky-800' : 'border-slate-300 text-slate-600'
                }`}
              >
                <Pastille couleur={infirmier.couleur} />
                {infirmier.prenom}
              </button>
            )
          })}
          <button
            type="button"
            aria-pressed={filtreInfirmiers.includes('non-affectee')}
            onClick={() =>
              changerParametre(
                'infirmier',
                filtreInfirmiers.includes('non-affectee')
                  ? filtreInfirmiers.filter((x) => x !== 'non-affectee')
                  : [...filtreInfirmiers, 'non-affectee'],
              )
            }
            className={`rounded-full border px-2.5 py-1 text-xs ${
              filtreInfirmiers.includes('non-affectee')
                ? 'border-amber-500 bg-amber-50 text-amber-800'
                : 'border-dashed border-slate-400 text-slate-600'
            }`}
          >
            Non affectées
          </button>
          {filtreInfirmiers.length > 0 || filtreStatut || recherchePatient ? (
            <Bouton
              variante="discret"
              onClick={() => setParametres(new URLSearchParams({ lundi }), { replace: true })}
            >
              Effacer les filtres
            </Bouton>
          ) : null}
        </fieldset>
      </div>

      <BandeauCouverture
        jours={semaine.jours}
        onAller={(date) => {
          toast(`Créneau du ${formatDate(date)} — voir la colonne correspondante.`, 'info')
        }}
      />

      {!modifiable ? (
        <Bandeau ton="info">
          Vous consultez le planning global en lecture seule. Vos pointages se font depuis
          « Ma tournée ».
        </Bandeau>
      ) : null}

      {/* ── Grille et panneau ── */}
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 flex-1">
          {semaine.totaux.nb_interventions === 0 ? (
            <EtatVide
              icone="🗓️"
              titre="Aucune intervention cette semaine"
              description="Le planning se génère depuis les ordonnances des patients."
            />
          ) : (
            <GrilleSemaine
              semaine={semaine}
              interventionsParJour={interventionsParJour}
              interventionSelectionnee={selection?.id ?? null}
              interactive={modifiable}
              onOuvrir={setSelection}
              onDeplacer={deplacer}
            />
          )}
        </div>

        <PanneauAReaffecter jours={semaine.jours} onOuvrir={setSelection} />
      </div>

      {/* ── Totaux par infirmier ── */}
      <div className="flex flex-wrap gap-2">
        {semaine.totaux.par_infirmier.map((total) => {
          const infirmier = semaine.infirmiers.find((i) => i.id === total.utilisateur_id)
          if (!infirmier) return null
          return (
            <div
              key={total.utilisateur_id}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <Pastille couleur={infirmier.couleur} />
              <span className="font-medium text-slate-800">
                {infirmier.prenom} {infirmier.nom}
              </span>
              <Etiquette>{total.nb_interventions} interv.</Etiquette>
              <span className="text-slate-500">{formatDuree(total.duree_totale_minutes)}</span>
            </div>
          )
        })}
      </div>

      <PanneauIntervention
        intervention={selection}
        infirmiers={semaine.infirmiers}
        typesSoins={requeteSoins.data?.items ?? []}
        amplitude={semaine.amplitude}
        modifiable={modifiable}
        enCours={modifier.isPending}
        onFermer={() => setSelection(null)}
        onDeplacer={(date, heure, utilisateurId) => {
          if (!selection) return
          modifier.mutate({
            id: selection.id,
            corps: {
              ...(date ? { date } : {}),
              heure_debut: heure,
              utilisateur_id: utilisateurId,
            },
          })
          setSelection(null)
        }}
        onChangerSoins={(soins) => {
          if (!selection) return
          modifier.mutate({ id: selection.id, corps: { soins } })
        }}
      />

      {/* ── Dialogue de conflit ── */}
      <Modale
        ouverte={conflit !== null}
        titre="Conflit de planning"
        description={conflit?.message}
        onFermer={() => setConflit(null)}
      >
        <p className="mb-4 text-sm text-slate-600">
          Vous pouvez enregistrer malgré tout. Le conflit sera alors{' '}
          <strong>consigné dans le journal d&apos;audit</strong>.
        </p>
        <div className="flex justify-end gap-2">
          <Bouton variante="secondaire" onClick={() => setConflit(null)}>
            Annuler
          </Bouton>
          <Bouton variante="danger" onClick={() => conflit?.rejouer()}>
            Forcer malgré le conflit
          </Bouton>
        </div>
      </Modale>
    </div>
  )
}
